/**
 * useAccounting.js — the chart of accounts, loaded once and shared.
 *
 * WHY A CONTEXT AND NOT A HOOK PER SCREEN
 * Groups, ledgers and period locks are read by nearly every screen in the
 * accounting module, and by the voucher grid on every keystroke. Three
 * listeners per screen would be a dozen listeners on a dashboard, and worse,
 * each screen would build its own ledger index — the same tree walk, repeated,
 * arriving at answers that drift the first time one of them is fixed.
 *
 * One provider high in the tree, one index, one answer.
 *
 * The provider is mounted inside the app shell rather than at the root, so an
 * unauthenticated visitor and the public shop window never open a listener on a
 * tenant's chart of accounts.
 */

import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCollection } from '../../hooks/useFirestore';
import { buildLedgerIndex } from '../../lib/ledgers';
import { groupOptions, indexGroups } from '../../lib/ledger-groups';
import { groupsQuery, ledgersQuery } from './ledger-repository';
import { periodLocksQuery } from './period-repository';

const AccountingContext = createContext(null);

export function AccountingProvider({ children }) {
  const { businessId } = useAuth();

  const groupQ = useMemo(() => groupsQuery(businessId), [businessId]);
  const ledgerQ = useMemo(() => ledgersQuery(businessId), [businessId]);
  const lockQ = useMemo(() => periodLocksQuery(businessId), [businessId]);

  const groups = useCollection(groupQ, { deps: [businessId] });
  const ledgers = useCollection(ledgerQ, { deps: [businessId] });
  const locks = useCollection(lockQ, { deps: [businessId] });

  const value = useMemo(() => {
    const groupList = (groups.data || []).filter((g) => !g.isArchived);
    const ledgerList = ledgers.data || [];
    const { byId, groupIndex, list } = buildLedgerIndex(ledgerList, groupList);

    return {
      loading: groups.loading || ledgers.loading,
      error: groups.error || ledgers.error,
      errorMessage: groups.errorMessage || ledgers.errorMessage,

      groups: groupList,
      groupIndex,
      groupOptions: groupOptions(groupList),

      /** Every ledger, including archived ones — a report may still name one. */
      ledgers: list,
      ledgerIndex: byId,
      /** What a picker should offer. */
      activeLedgers: list.filter((l) => !l.isArchived),

      /** Locks that are still in force. A released one is history, not a rule. */
      locks: (locks.data || []).filter((lock) => !lock.isReleased),
      allLocks: locks.data || [],

      /** False until the chart of accounts has been seeded. */
      isSeeded: groupList.length > 0,
    };
  }, [groups, ledgers, locks]);

  return <AccountingContext.Provider value={value}>{children}</AccountingContext.Provider>;
}

/**
 * The chart of accounts.
 *
 * Returns an empty, non-throwing shape when the provider is absent, so a
 * component rendered outside the shell (a test, a print view) degrades to
 * "no ledgers" rather than to a white screen.
 */
export function useAccounting() {
  return (
    useContext(AccountingContext) || {
      loading: false,
      error: null,
      errorMessage: null,
      groups: [],
      groupIndex: indexGroups([]),
      groupOptions: [],
      ledgers: [],
      ledgerIndex: new Map(),
      activeLedgers: [],
      locks: [],
      allLocks: [],
      isSeeded: false,
    }
  );
}


